import { Component, useEffect, useRef, useState } from "react";
import { AlertTriangle, ChevronDown, Info, Loader2, Mic, MicOff, PhoneCall, PhoneOff, Save } from "lucide-react";
import { ConversationProvider, useConversation } from "@elevenlabs/react";
import { api } from "@/lib/api";
import { Waveform } from "@/components/bits";

/**
 * In-browser voice test (ElevenLabs Agents).
 *
 * Flow: builder → POST /api/agents/:id/voice-test-session → WebRTC conversation
 * token (preferred) or signed URL (websocket fallback) → `startSession(...)`
 * from @elevenlabs/react. Unsaved (dirty) builder state is sent as `{ agent }`
 * so the backend can auto-save before provisioning the ElevenLabs agent
 * (10–30s cold start).
 */
const PHASE_LABEL = {
  provisioning: "Provisioning voice agent…",
  starting: "Connecting the call…",
  live: "Live call",
  ended: "Call ended",
  error: "Voice test failed",
  unconfigured: "ElevenLabs not configured",
  idle: "Ready",
};

const fmtClock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function VoiceTestPanel(props) {
  return (
    <SDKBoundary>
      <ConversationProvider>
        <Panel {...props} />
      </ConversationProvider>
    </SDKBoundary>
  );
}

function Panel({ agent, agentId, dirty, onNeedSave }) {
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState("");
  const [session, setSession] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [showVars, setShowVars] = useState(false);
  const endRef = useRef(null);
  const phaseRef = useRef(phase);

  const conversation = useConversation();
  const status = conversation.status;

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Hook status transitions → panel phase.
  useEffect(() => {
    if (status === "connected" && (phase === "starting" || phase === "provisioning")) setPhase("live");
    if (status === "disconnected" && phase === "live") setPhase("ended");
  }, [status, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status === "error" && conversation.message) {
      setError(String(conversation.message));
      setPhase("error");
    }
  }, [status, conversation.message]); // eslint-disable-line react-hooks/exhaustive-deps

  // Call timer while live.
  useEffect(() => {
    if (phase !== "live") return undefined;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [phase]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [transcript]);

  const pushMessage = ({ role, message }) => {
    const text = String(message || "").trim();
    if (!text) return;
    const who = role === "user" ? "user" : "agent";
    setTranscript((list) => {
      const last = list[list.length - 1];
      if (last && last.role === who) {
        // Streamed parts / corrections of the same turn.
        if (text === last.text || last.text.startsWith(text)) return list;
        if (text.startsWith(last.text)) return [...list.slice(0, -1), { ...last, text }];
      }
      return [...list, { id: `${list.length}-${text.slice(0, 12)}`, role: who, text }];
    });
  };

  /** Surface *why* a session ended instead of silently flipping back to idle. */
  const handleDisconnect = (details) => {
    const d = details || {};
    const bits = [d.reason === "error" ? (d.message || "session error") : d.reason,
                  d.closeCode ? `close ${d.closeCode}` : "", d.closeReason || ""].filter(Boolean);
    const detailText = bits.join(" · ");
    if (d.reason === "error") {
      setError(`The voice session ended with an error${detailText ? ` (${detailText})` : ""}.`);
      setPhase("error");
      return;
    }
    if (phaseRef.current === "starting" || phaseRef.current === "provisioning") {
      setError(`The session closed before it connected${detailText ? ` (${detailText})` : ""}. Usually the agent rejected the session start — check the dynamic variables and tools on the ElevenLabs side.`);
      setPhase("error");
      return;
    }
    setPhase("ended");
  };

  const start = async () => {
    if (!agentId) return;
    setError(""); setTranscript([]); setElapsed(0); setSession(null);
    setPhase("provisioning");
    try {
      const body = dirty && agent ? { agent } : {};
      const { data } = await api.post(`/agents/${agentId}/voice-test-session`, body, { timeout: 90000 });
      if (data?.configured === false) { setError(data.detail || "ElevenLabs is not configured."); setPhase("unconfigured"); return; }
      if (data?.error) { setError(data.error); setPhase("error"); return; }
      if (!data?.conversation_token && !data?.signed_url) { setError("The backend did not return a session token."); setPhase("error"); return; }
      // WebRTC (conversation token) is the supported in-browser voice transport in
      // @elevenlabs/react v1.x; signed URLs ride the older websocket path.
      const connection = data.conversation_token ? "webrtc" : "websocket";
      setSession({ ...data, connection });
      setPhase("starting");
      conversation.startSession({
        ...(data.conversation_token
          ? { conversationToken: data.conversation_token, connectionType: "webrtc" }
          : { signedUrl: data.signed_url, connectionType: "websocket" }),
        dynamicVariables: data.dynamic_variables || {},
        onMessage: pushMessage,
        onError: (msg) => { setError(String(msg || "Voice session error")); setPhase("error"); },
        onDisconnect: handleDisconnect,
        onDebug: (info) => console.debug("[voice-test]", info),
      });
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Could not start the voice test.");
      setPhase("error");
    }
  };

  const end = () => {
    try { conversation.endSession(); } catch (e) { /* session already closed */ }
    setPhase("ended");
  };

  const busy = phase === "provisioning" || phase === "starting";
  const live = phase === "live";
  const hasSession = Boolean(session) || live;
  const vars = session?.dynamic_variables || {};

  return (
    <div className="glass p-5 rise space-y-4" data-testid="voice-test-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`relative w-10 h-10 shrink-0 ${live ? "pulse-ring" : ""}`}>
            <div className={`absolute inset-0 rounded-full flex items-center justify-center ${live ? "bg-emerald-500" : "bg-white/[0.06] border border-white/10"}`}>
              <PhoneCall className={`w-4 h-4 ${live ? "text-[#06110A]" : "text-emerald-300"}`} />
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-100">Live voice test</p>
            <p className="text-[11px] font-mono text-slate-500 truncate">{agent?.name || "Agent"} · {agent?.language || "—"} · {agent?.dialect || "—"}</p>
          </div>
        </div>
        <span className="flex items-center gap-2 shrink-0" data-testid="voice-test-status">
          <span className={`w-2 h-2 rounded-full ${dotFor(phase)}`} />
          <span className="text-[11px] font-mono uppercase tracking-wide text-slate-500">{PHASE_LABEL[phase]}</span>
        </span>
      </div>

      {!agentId && (
        <div className="glass-soft p-4 flex flex-col sm:flex-row sm:items-center gap-3 border border-amber-500/25" data-testid="voice-test-needs-save">
          <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-slate-100">Save the agent first</p>
            <p className="text-[11px] text-slate-400 mt-0.5">Voice tests run on the saved config — save this draft, then start the test.</p>
          </div>
          {onNeedSave && <button onClick={onNeedSave} className="btn-gold btn-sm shrink-0" data-testid="voice-test-save-btn"><Save className="w-3.5 h-3.5" /> Save agent</button>}
        </div>
      )}

      {phase === "unconfigured" && (
        <div className="glass-soft p-4 flex gap-3 border border-amber-500/25" data-testid="voice-test-unconfigured">
          <Info className="w-5 h-5 text-amber-300 shrink-0" />
          <div>
            <p className="text-sm text-slate-100">ElevenLabs not configured — add ELEVENLABS_API_KEY</p>
            <p className="text-[11px] text-slate-400 mt-1">{error}</p>
          </div>
        </div>
      )}

      {phase === "error" && (
        <div className="glass-soft p-4 flex gap-3 border border-rose-500/25" data-testid="voice-test-error">
          <AlertTriangle className="w-5 h-5 text-rose-300 shrink-0" />
          <div>
            <p className="text-sm text-slate-100">Voice test failed</p>
            <p className="text-[11px] text-slate-400 mt-1 break-words">{error}</p>
          </div>
        </div>
      )}

      {busy && (
        <div className="glass-soft p-4 flex items-center gap-3" data-testid="voice-test-provisioning">
          <Loader2 className="w-4 h-4 text-emerald-300 animate-spin" />
          <div>
            <p className="text-sm text-slate-100">{PHASE_LABEL[phase]}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {phase === "provisioning" ? "First publish on ElevenLabs can take 10–30 seconds." : "Ask the mic permission if the browser prompts you."}
            </p>
          </div>
        </div>
      )}

      {(live || (hasSession && phase === "ended")) && (
        <div className="space-y-3" data-testid="voice-test-softphone">
          <div className="glass-soft overflow-hidden">
            <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2 flex-wrap">
              <span className={`w-2 h-2 rounded-full ${live ? "bg-emerald-400 pulse-ring" : "bg-slate-600"}`} />
              <span className="text-[11px] font-mono uppercase tracking-wide text-slate-400">{live ? (conversation.mode === "speaking" ? "Agent speaking" : "Listening") : PHASE_LABEL[phase]}</span>
              {live && conversation.isSpeaking && <Waveform bars={7} className="h-4" />}
              <span className="ml-auto font-mono text-[11px] text-slate-500" data-testid="voice-test-timer">{fmtClock(elapsed)}</span>
            </div>
            <div className="p-3 space-y-2 h-56 overflow-y-auto" data-testid="voice-transcript">
              {transcript.map((m) => (
                <div key={m.id} className={m.role === "agent" ? "wa-bubble-agent whitespace-pre-wrap" : "wa-bubble-contact whitespace-pre-wrap"}>{m.text}</div>
              ))}
              {!transcript.length && <p className="text-xs text-slate-500">Transcript appears here as you speak. Say “hello” to start.</p>}
              <div ref={endRef} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {live && (
              <>
                <button onClick={() => conversation.setMuted(!conversation.isMuted)} className="btn-ghost flex-1" data-testid="voice-test-mute-btn" title={conversation.isMuted ? "Unmute your mic" : "Mute your mic"}>
                  {conversation.isMuted ? <MicOff className="w-4 h-4 text-rose-300" /> : <Mic className="w-4 h-4" />} {conversation.isMuted ? "Unmute" : "Mute"}
                </button>
                <button onClick={end} className="btn-gold flex-1" data-testid="voice-test-end-btn"><PhoneOff className="w-4 h-4" /> End call</button>
              </>
            )}
          </div>
        </div>
      )}

      {!busy && !live && (
        <button onClick={start} disabled={!agentId} className="btn-primary w-full" data-testid="voice-test-start-btn" title={agentId ? "Runs a real ElevenLabs conversation in your browser" : "Save the agent first"}>
          <PhoneCall className="w-4 h-4" /> {phase === "ended" || hasSession ? "Start another voice test" : "Start voice test"}
        </button>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><Info className="w-3 h-3" /> Voice tests use your ElevenLabs minutes.</p>
        <p className="text-[11px] font-mono text-slate-500">{session ? `${session.connection || "webrtc"} · ${session.language} (${session.dialect})` : `${agent?.language || "—"} · ${agent?.dialect || "—"}`}</p>
      </div>

      {dirty && agentId && (
        <p className="text-[11px] text-amber-300/90" data-testid="voice-test-dirty-note">Unsaved changes will be auto-saved to the agent before the call is provisioned.</p>
      )}

      {Boolean(Object.keys(vars).length) && (
        <div className="glass-soft overflow-hidden" data-testid="voice-test-context">
          <button type="button" onClick={() => setShowVars((s) => !s)} className="w-full flex items-center justify-between px-3 py-2 text-left">
            <span className="text-[11px] font-mono uppercase tracking-wide text-slate-400">Test caller context · {Object.keys(vars).length} variables</span>
            <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${showVars ? "rotate-180" : ""}`} />
          </button>
          {showVars && (
            <div className="px-3 pb-3 grid sm:grid-cols-2 gap-x-4 gap-y-1">
              {Object.entries(vars).map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-2 border-b border-white/5 py-1">
                  <span className="text-[11px] font-mono text-slate-500 truncate">{k}</span>
                  <span className="text-[11px] text-slate-300 truncate max-w-[60%] text-right">{String(v ?? "")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function dotFor(phase) {
  if (phase === "live") return "bg-emerald-400";
  if (phase === "provisioning" || phase === "starting") return "bg-amber-400";
  if (phase === "error" || phase === "unconfigured") return "bg-rose-400";
  return "bg-slate-600";
}

/** Renders a clear message instead of crashing if the voice SDK fails to load. */
class SDKBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(err) {
    console.error("VoiceTestPanel: @elevenlabs/react failed", err);
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="glass p-5 rise" data-testid="voice-test-sdk-missing">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0" />
            <div>
              <p className="text-sm text-slate-100">voice SDK missing — run <code className="text-emerald-300">yarn add @elevenlabs/react</code></p>
              <p className="text-[11px] text-slate-500 mt-1">The text chat test above still works. Install the SDK in <code>frontend/</code> and restart the dev server to run live voice tests.</p>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
